/**
 * Rechargement du Feed — source unique, sur le modèle de `lib/progress-sync.ts`.
 *
 * Toutes les sources du Feed vivent dans le cache React Query sous une clé
 * `news-*` (cf. `use-news-feed.ts`). Recharger le Feed = marquer périmées
 * toutes ces clés d'un coup : la page affichée se recharge aussitôt.
 *
 * Trois déclencheurs, du plus explicite au plus discret :
 *  1. le bouton « Actualiser » et le tirer-pour-rafraîchir (geste de l'utilisateur) ;
 *  2. le retour de l'app au premier plan / de l'onglet au premier plan ;
 *  3. l'arrivée d'une notification push pendant que l'app est ouverte
 *     (`notificationReceived`, main.tsx) — c'est le « temps réel » : le serveur
 *     envoie déjà un push pour chaque nouveauté du Feed, on s'en sert comme
 *     signal au lieu d'ouvrir un abonnement Realtime par table.
 *
 * Ajouter une source au Feed = ajouter son préfixe de clé ici, une seule fois.
 */
import type { QueryClient } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';

/** Préfixes de clés de toutes les sources du Feed + ce que la page affiche avec. */
export const FEED_KEYS = [
  'news-assignments',
  'news-challenges',
  'news-attempts',
  'news-started-attempts',
  'news-submissions',
  'news-visio',
  'news-evaluations',
  'news-diagnostics',
  'news-question-reports',
  'news-blog',
  'news-daily-recaps',
  'news-authors',
  'news-assignments-done',
  // listes chargées d'un coup (pas paginées) mais affichées dans le Feed
  'discussions',
  'student-skillcards',
  'my-private-lives',
  // date de dernière ouverture → recalcul des pastilles « nouveau »
  'feed-seen-at',
] as const;

/** Marque périmées toutes les données du Feed. Les écrans affichés rechargent. */
export function invalidateFeed(client: QueryClient = queryClient): void {
  for (const key of FEED_KEYS) void client.invalidateQueries({ queryKey: [key] });
}

/**
 * Recharge le Feed et ATTEND que ce soit fini (bouton « Actualiser »,
 * tirer-pour-rafraîchir : il faut savoir quand arrêter l'indicateur).
 * Ne recharge que les requêtes réellement affichées (`type: 'active'`) — les
 * autres restent simplement périmées.
 */
export async function refetchFeed(client: QueryClient = queryClient): Promise<void> {
  await Promise.allSettled(
    FEED_KEYS.map((key) => client.refetchQueries({ queryKey: [key], type: 'active' })),
  );
}
